<script setup lang="ts">
import SeminarDetailDescriptionSection from './SeminarDetailDescriptionSection.vue';
import SeminarDetailEntryButton from './SeminarDetailEntryButton.vue';
import SeminarDetailHeading from './SeminarDetailHeading.vue';
import SeminarDetailPageContainer from './SeminarDetailPageContainer.vue';
import SeminarDetailSeminarsSection from './SeminarDetailSeminarsSection.vue';
import SeminarDetailSpeakersSection from './SeminarDetailSpeakersSection.vue';
import SeminarDetailSummarySection from './SeminarDetailSummarySection.vue';
import BreadcrumbsStrip from '~/components/molecules/BreadcrumbsStrip/BreadcrumbsStrip.vue';
import type { Breadcrumbs } from '~/components/molecules/BreadcrumbsStrip/BreadcrumbsStrip.vue';
import { graphql } from '~/gql';
import { SeminarApplicationStatus } from '~/gql/graphql';

const route = useRoute();
const seminarId = computed(() => {
  const id = getStringQuery(route.params['id']);
  if (id == null) {
    throw createError({ statusCode: 404 });
  }
  return id;
});

const query = graphql(`
  query SeminarDetailSeminarsQuery($id: ID!) {
    filterSeminars(
      first: 20
      after: null
      filter: { isApplicable: true, is400F: true }
    ) {
      edges {
        node {
          ...SeminarDetailSeminarsSectionSeminar
        }
      }
    }
    seminar(id: $id) {
      id
      title
      description
      coverImage
      categories
      timerexUrl: timerexURL
      applicationStatus
      schedules {
        applicationStatus
      }
      ...SeminarDetailDescriptionSectionSeminar
      ...SeminarDetailSummarySectionSeminar
      speakers {
        ...SeminarDetailSpeakersSectionSpeaker
      }
    }
  }
`);

const { data, status, execute } = useLazyAsyncQuery(query, {
  id: seminarId.value ?? '',
});
await execute();

watch(
  status,
  () => {
    if (
      status.value === 'error' ||
      (status.value === 'success' && data.value?.seminar == null)
    ) {
      throw createError({ statusCode: 404 });
    }
  },
  { immediate: true }
);

const seminars = computed(
  () =>
    data.value?.filterSeminars.edges
      .slice()
      .sort(() => 0.5 - Math.random())
      .slice(0, 3)
      .map((elm) => elm.node) ?? []
);

const breadcrumbs = computed<Breadcrumbs>(() => [
  {
    location: '/',
    label: 'HOME',
    description: '',
  },
  {
    location: '/seminar',
    label: 'オカネコマネーセミナー',
    description: '',
  },
  {
    label: data?.value?.seminar?.title ?? '',
    description: '',
  },
]);

const hasActiveSchedule = computed(() =>
  data.value?.seminar?.schedules.some(
    (s) => s.applicationStatus === SeminarApplicationStatus.Applicable
  )
);

const isEntryButtonDisabled = computed(
  () =>
    (!hasActiveSchedule.value && data.value?.seminar?.timerexUrl == null) ||
    data.value?.seminar?.applicationStatus !==
      SeminarApplicationStatus.Applicable
);

const { buildImgixURL } = useImgixURL();

const ogImageUrl = computed(() =>
  data?.value?.seminar?.coverImage
    ? buildImgixURL(data?.value?.seminar?.coverImage, {
        width: 1200,
        quality: 100,
      })
    : undefined
);

useSeminarAppMeta({
  title: data?.value?.seminar?.title,
  description:
    (data?.value?.seminar?.description.length ?? 0) > 80
      ? data?.value?.seminar?.description.substring(0, 80) + '…'
      : data?.value?.seminar?.description,
  imageUrl: ogImageUrl,
});
</script>

<template>
  <div v-if="!data?.seminar" />
  <SeminarDetailPageContainer v-else :class="$style.container">
    <BreadcrumbsStrip
      :class="$style.container__breadcrumbs"
      :breadcrumbs="breadcrumbs"
    />
    <SeminarDetailHeading
      :class="$style.container__heading"
      :heading="data.seminar.title"
      :seminar-categories="data.seminar.categories"
    />
    <div :class="[$style.columns, $style.container__columns]">
      <SeminarDetailDescriptionSection
        :class="$style.columns__left"
        :seminar="data.seminar"
        :is-entry-button-disabled="isEntryButtonDisabled"
      />
      <div :class="$style.columns__right">
        <SeminarDetailSpeakersSection :speakers="data.seminar.speakers" />
        <SeminarDetailSummarySection
          :class="$style.container__summary"
          :seminar="data.seminar"
        />
      </div>
    </div>
    <div :class="$style.container__link">
      <NuxtLink href="/seminar">オカネコマネーセミナートップへ戻る</NuxtLink>
    </div>
    <SeminarDetailSeminarsSection
      :class="$style.container__seminars"
      :seminars="seminars"
    />
    <SeminarDetailEntryButton
      :class="$style.container__button"
      :seminar-id="data.seminar.id"
      :timerex-url="data.seminar.timerexUrl ?? undefined"
      :disabled="isEntryButtonDisabled"
      variant="whiteBg"
    />
  </SeminarDetailPageContainer>
</template>

<style module lang="scss">
.container {
  &__breadcrumbs {
    border-bottom: 1px solid $color-border-low-emphasis;
    padding: 2px;
  }
  &__heading {
    margin-top: 32px;
    @include mobile-only {
      margin-top: 16px;
    }
  }
  &__link {
    margin-top: 32px;
    text-decoration: underline;
    font-size: 16px;
    text-align: center;
    line-height: 1.8;

    @include mobile-only {
      margin-top: 24px;
    }
  }

  &__columns {
    margin-top: 32px;
    @include mobile-only {
      margin-top: 16px;
    }
  }

  &__summary {
    margin-top: 24px;
  }

  &__seminars {
    margin-top: 56px;
    @include mobile-only {
      margin-top: 24px;
    }
  }

  &__button {
    margin-top: 56px;
    position: sticky;
    bottom: 0;
  }
}

.columns {
  display: flex;
  gap: 32px;

  &__left {
    flex: 0 1 720px;
    @include mobile-only {
      flex: 0;
    }
  }

  &__right {
    flex: 1;
    @include mobile-only {
      flex: 0;
    }
  }

  @include mobile-only {
    flex-direction: column;
  }
}
</style>
